import { useEffect, useRef, useState } from 'react'
import { Search } from 'lucide-react'
import { supabase } from '@/lib/supabase/client'

interface Manufacturer {
  id: string
  name: string
}

interface ManufacturerSearchFieldProps {
  value: string
  onChange: (id: string) => void
  autoFocus?: boolean
  disabled?: boolean
  variant?: 'field' | 'chip'
  placeholder?: string
}

const fieldCls =
  'w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#1E3A5F] bg-white text-slate-800 transition-colors'
const chipCls =
  'w-full text-sm text-slate-800 bg-transparent focus:outline-none border-b border-slate-200 focus:border-[#1E3A5F] py-0.5 transition-colors'

const ManufacturerSearchField = ({
  value,
  onChange,
  autoFocus = false,
  disabled = false,
  variant = 'field',
  placeholder = 'Søk produsent...',
}: ManufacturerSearchFieldProps) => {
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([])
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const blurRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const { data } = await supabase
        .from('manufacturers_v2')
        .select('id, name')
        .order('name', { ascending: true })
      if (!cancelled) setManufacturers(data || [])
    }
    load()
    return () => { cancelled = true }
  }, [])

  const selected = manufacturers.find(m => m.id === value || m.name === value)
  const filterText = selected && query === selected.name ? '' : query
  const filtered = manufacturers.filter(m =>
    m.name.toLowerCase().includes(filterText.trim().toLowerCase()),
  )

  const isChip = variant === 'chip'

  return (
    <div className="relative">
      {!isChip && (
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      )}
      <input
        type="text"
        value={open ? query : (selected?.name || '')}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        onFocus={() => {
          if (blurRef.current) clearTimeout(blurRef.current)
          setQuery(selected?.name || '')
          setOpen(true)
        }}
        onBlur={() => {
          blurRef.current = setTimeout(() => setOpen(false), 150)
        }}
        onChange={e => {
          setQuery(e.target.value)
          setOpen(true)
          if (selected && e.target.value !== selected.name) onChange('')
        }}
        className={isChip ? chipCls : `${fieldCls} pl-9`}
        style={isChip ? undefined : { minHeight: 44 }}
      />
      {open && !disabled && (
        <div className="absolute z-30 left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg">
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-sm text-slate-400">Ingen treff</p>
          ) : (
            filtered.map(m => (
              <button
                key={m.id}
                type="button"
                onMouseDown={e => e.preventDefault()}
                onClick={() => {
                  onChange(m.id)
                  setQuery(m.name)
                  setOpen(false)
                }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${
                  m.id === selected?.id ? 'bg-[#1E3A5F]/5 text-[#1E3A5F] font-semibold' : 'text-slate-800'
                }`}
              >
                {m.name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default ManufacturerSearchField
